<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import {
  DEFAULT_STUDENT_CRITERIA,
  DEFAULT_TUTOR_CRITERIA,
  type FeedbackCriterion,
} from "@/types/feedback";
import { useFeedbackForm, getRatingLabel } from "@/composables/useFeedbackForm";
import {
  auth,
  onAuthStateChanged,
  db,
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  updateDoc,
} from "@/firebase";
import { feedbackService } from "@/services/feedbacks";
import { useRouter } from "vue-router";
import { checkContentSafety } from "@/utils/contentSafetyFilter";

const route = useRoute();
const router = useRouter();

// User state management
const currentUser = ref<any>(null);
const userType = ref<string | null>(null);
const isLoading = ref(true);
const isSubmitting = ref(false);
const error = ref<string | null>(null);
const success = ref(false);

// Reactive data - will be set based on user type
const criteria = ref<FeedbackCriterion[]>([]);

const additionalComments = ref("");
const profanityError = ref<string | null>(null);

// Mock session data - in real app, this would come from API or props
const sessionInfo = ref({
  studentName: "Student Name",
  tutorName: "Maria Isabella Santos",
  subject: "Mathematics",
  date: new Date().toLocaleDateString(),
  sessionId: (route.params.sessionId as string) || "session-123",
  studentId: "student-user-id", // Would come from session data
  tutorId: "current-user-id", // Would come from auth
});

// Computed properties
const averageRating = computed(() => {
  const validRatings = criteria.value.filter((criterion) => criterion.rating !== null);
  if (validRatings.length === 0) return "0.0";
  const total = validRatings.reduce((sum, criterion) => sum + (criterion.rating as number), 0);
  return (total / validRatings.length).toFixed(1);
});

const completedCount = computed(() => {
  // Count how many criteria have been explicitly rated (not null)
  return criteria.value.filter((criterion) => criterion.rating !== null).length;
});

const allCompleted = computed(() => {
  return completedCount.value === criteria.value.length;
});

const characterCount = computed(() => {
  return additionalComments.value.length;
});

const remainingCharacters = computed(() => {
  return 200 - characterCount.value;
});

// Conditional text based on user type
const evaluationTitle = computed(() => {
  return userType.value === "student" ? "Evaluate Your Tutor" : "Evaluate Your Student";
});

const performanceTitle = computed(() => {
  return userType.value === "student"
    ? "Rating Your Tutor's Performance"
    : "Rating Your Student's Performance";
});

const performanceDescription = computed(() => {
  return userType.value === "student"
    ? "Please rate your level of agreement with each statement about your tutoring experience."
    : "Please rate your level of agreement with each statement about your student's performance.";
});

const commentsTitle = computed(() => {
  return userType.value === "student"
    ? "Additional Comments & Suggestions (Optional)"
    : "Additional Observations & Recommendations (Optional)";
});

const commentsDescription = computed(() => {
  return userType.value === "student"
    ? "Share any specific feedback, suggestions for improvement, or highlight what worked particularly well during your tutoring session."
    : "Share specific observations about the student's learning style, areas for improvement, strengths to build upon, or recommendations for future sessions.";
});

const commentsPlaceholder = computed(() => {
  return userType.value === "student"
    ? "Share any specific feedback, suggestions for improvement, or highlight what worked particularly well during your tutoring session..."
    : "Share specific observations about the student's learning style, areas for improvement, strengths to build upon, or recommendations for future sessions...";
});

// Methods
const updateRating = (criterionId: string, rating: number) => {
  const criterion = criteria.value.find((c) => c.id === criterionId);
  if (criterion) {
    criterion.rating = rating;
  }
};

// Validate comments for profanity (real-time)
const validateComments = () => {
  if (!additionalComments.value || additionalComments.value.trim().length === 0) {
    profanityError.value = null;
    return;
  }
  
  const safetyCheck = checkContentSafety(additionalComments.value);
  if (!safetyCheck.isSafe || safetyCheck.blocked) {
    profanityError.value = safetyCheck.message || 'Your feedback contains inappropriate language. Please keep your comments professional and respectful.';
  } else {
    profanityError.value = null;
  }
};

const clearRating = (criterionId: string) => {
  const criterion = criteria.value.find((c) => c.id === criterionId);
  if (criterion) {
    criterion.rating = null;
  }
};

// Get user type from Firebase
const getUserType = async (uid: string) => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.userType || null;
    }
    return null;
  } catch (error) {
    console.error("Error fetching user type:", error);
    return null;
  }
};

// Look up user ID by name and type (fallback method)
const getUserIdByNameAndType = async (
  name: string,
  userType: "student" | "tutor"
): Promise<string | null> => {
  try {
    // Split the name to search for users
    const nameParts = name.trim().split(" ");
    if (nameParts.length < 2) {
      console.warn("Name should have at least first and last name:", name);
      return null;
    }

    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];

    // Query users collection for matching name and userType
    const usersRef = collection(db, "users");
    const q = query(
      usersRef,
      where("firstName", "==", firstName),
      where("lastName", "==", lastName),
      where("userType", "==", userType)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.warn(`No user found with name "${name}" and type "${userType}"`);
      return null;
    }

    if (querySnapshot.docs.length > 1) {
      console.warn(
        `Multiple users found with name "${name}" and type "${userType}". Using the first one.`
      );
    }

    return querySnapshot.docs[0].id;
  } catch (error) {
    console.error("Error looking up user by name:", error);
    return null;
  }
};

// Get session data from Firebase sessions collection
const getSessionData = async (sessionId: string) => {
  try {
    // Try to get session from Firebase sessions collection
    const sessionDoc = await getDoc(doc(db, "sessions", sessionId));
    if (sessionDoc.exists()) {
      return sessionDoc.data();
    }

    // If not found in sessions, try tutoring_sessions collection
    const tutoringSessionDoc = await getDoc(doc(db, "tutoring_sessions", sessionId));
    if (tutoringSessionDoc.exists()) {
      return tutoringSessionDoc.data();
    }

    return null;
  } catch (error) {
    console.error("Error fetching session data:", error);
    return null;
  }
};

// Initialize criteria based on user type
const initializeCriteria = (userType: string) => {
  const baseCriteria = userType === "student" ? DEFAULT_TUTOR_CRITERIA : DEFAULT_STUDENT_CRITERIA;
  criteria.value = baseCriteria.map((criterion) => ({
    ...criterion,
    rating: null as number | null,
  }));
};

// Navigation function
const navigateToSessions = async () => {
  // Determine where to navigate based on user type
  if (userType.value === "tutor") {
    router.push("/tutor/sessions");
  } else {
    router.push("/student/sessions");
  }
};

const handleSubmitFeedback = async () => {
  if (!currentUser.value || !userType.value) {
    error.value = "User not authenticated";
    return;
  }

  if (!allCompleted.value) {
    error.value = "Please complete all ratings before submitting";
    return;
  }

  // Validate comments for profanity before submitting
  if (additionalComments.value && additionalComments.value.trim().length > 0) {
    const safetyCheck = checkContentSafety(additionalComments.value);
    if (!safetyCheck.isSafe || safetyCheck.blocked) {
      const errorMessage = safetyCheck.message || 'Your feedback contains inappropriate language. Please keep your comments professional and respectful.';
      profanityError.value = errorMessage;
      error.value = errorMessage;
      // Scroll to comments section to show error
      const commentsSection = document.querySelector('.comments-section');
      if (commentsSection) {
        commentsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
  }

  // Clear any previous profanity errors
  profanityError.value = null;

  isSubmitting.value = true;
  error.value = null;
  success.value = false;

  try {
    const sessionId = (route.params.sessionId as string) || "session-123";
    const fromUserId = currentUser.value.uid;

    // Get the correct toUserId based on user type and session data
    let toUserId: string | null = null;

    // Try to get real session data if possible
    try {
      const sessionData = await feedbackService.getSessionFeedbackData(sessionId);
      toUserId =
        userType.value === "student" ? sessionData.session.tutorId : sessionData.session.studentId;
      console.log("Got session data from service, toUserId:", toUserId);
    } catch (sessionError) {
      console.warn(
        "Could not get session data from service, trying Firebase session lookup:",
        sessionError
      );

      // Try to get session data from Firebase and then look up user IDs
      try {
        const sessionData = await getSessionData(sessionId);

        if (sessionData) {
          if (userType.value === "student") {
            // Student is evaluating tutor - get tutorId or look up by tutorName
            if (sessionData.tutorId) {
              toUserId = sessionData.tutorId;
            } else if (sessionData.tutorName) {
              toUserId = await getUserIdByNameAndType(sessionData.tutorName, "tutor");
            }
          } else {
            // Tutor is evaluating student - get studentId or look up by studentName
            if (sessionData.studentId) {
              toUserId = sessionData.studentId;
            } else if (sessionData.studentName) {
              toUserId = await getUserIdByNameAndType(sessionData.studentName, "student");
            }
          }

          if (toUserId) {
            console.log("Successfully found toUserId from Firebase session data:", toUserId);
          }
        }

        // If still no toUserId found, use fallback
        if (!toUserId) {
          console.warn("Could not find user ID from session data, using fallback approach");
          toUserId = userType.value === "student" ? "tutor-mock-id" : "student-mock-id";
        }
      } catch (lookupError) {
        console.error("Failed to lookup user from session data:", lookupError);
        // Final fallback
        toUserId = userType.value === "student" ? "tutor-mock-id" : "student-mock-id";
      }
    }

    if (!toUserId) {
      throw new Error("Could not determine the user being evaluated");
    }

    // Create feedback document in Firebase
    const feedbackRef = await addDoc(collection(db, "feedbacks"), {
      sessionID: sessionId,
      fromUserID: fromUserId,
      toUserID: toUserId,
      overallScore: parseFloat(averageRating.value),
      comment: additionalComments.value.trim() || "",
      status: "active",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      archivedAt: null,
    });

    // Create individual rating entries in the ratings subcollection
    const ratingsCollection = collection(feedbackRef, "ratings");

    for (const criterion of criteria.value) {
      if (criterion.rating !== null) {
        await addDoc(ratingsCollection, {
          CriteriaCode: criterion.id,
          RatingValue: criterion.rating,
          createdAt: serverTimestamp(),
        });
      }
    }

    console.log("Feedback created successfully with ID:", feedbackRef.id);

    // Update the session to mark feedback as given
    try {
      const sessionRef = doc(db, "sessions", sessionId);
      await updateDoc(sessionRef, {
        [`feedbackStatus_${fromUserId}`]: "given",
        updatedAt: serverTimestamp(),
      });
      console.log("Session feedbackStatus updated successfully");
    } catch (sessionUpdateError) {
      console.error("Error updating session feedbackStatus:", sessionUpdateError);
      // Don't fail the whole operation if session update fails
    }

    // Create audit log for feedback submission
    try {
      const { createAuditLog } = await import("@/services/auditLogService");

      // Get user names for the audit log
      let fromUserName = "User";
      let fromUserEmail = "";
      let toUserName = "User";
      let toUserEmail = "";

      // Get current user data
      if (currentUser.value) {
        const currentUserDoc = await getDoc(doc(db, "users", currentUser.value.uid));
        if (currentUserDoc.exists()) {
          const userData = currentUserDoc.data();
          fromUserName =
            userData.displayName ||
            `${userData.firstName || ""} ${userData.lastName || ""}`.trim() ||
            userData.email ||
            "User";
          fromUserEmail = userData.email || "";
        }
      }

      // Get recipient user data
      const toUserDoc = await getDoc(doc(db, "users", toUserId));
      if (toUserDoc.exists()) {
        const userData = toUserDoc.data();
        toUserName =
          userData.displayName ||
          `${userData.firstName || ""} ${userData.lastName || ""}`.trim() ||
          userData.email ||
          "User";
        toUserEmail = userData.email || "";
      }

      await createAuditLog({
        action: userType.value === "student" ? "student_feedback" : "tutor_feedback",
        type: "system_event",
        details: `${
          userType.value === "student" ? "Student" : "Tutor"
        } ${fromUserName} submitted feedback and rating (${averageRating.value}/5.0) for ${
          userType.value === "student" ? "tutor" : "student"
        } ${toUserName}${additionalComments.value ? " with additional comments" : ""}`,
        performedById: currentUser.value?.uid,
        performedByName: fromUserName,
        performedByEmail: fromUserEmail,
        affectedUserId: toUserId,
        affectedUserName: toUserName,
        affectedUserEmail: toUserEmail,
        metadata: {
          sessionId: sessionId,
          feedbackId: feedbackRef.id,
          overallRating: parseFloat(averageRating.value),
          criteriaCount: criteria.value.filter((c) => c.rating !== null).length,
          hasComments: !!additionalComments.value.trim(),
          actionType: "feedback_submission",
          evaluatorType: userType.value,
          evaluatedType: userType.value === "student" ? "tutor" : "student",
        },
      });
      console.log("Audit log created for feedback submission");
    } catch (auditError) {
      console.error("Failed to create audit log for feedback submission:", auditError);
      // Don't fail the whole operation if audit log creation fails
    }

    success.value = true;

    // Redirect after a short delay
    setTimeout(() => {
      navigateToSessions();
    }, 2000);
  } catch (err) {
    console.error("Error creating feedback:", err);
    error.value = err instanceof Error ? err.message : "Failed to submit feedback";
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  // Listen for auth state changes and get user type
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      currentUser.value = user;
      const fetchedUserType = await getUserType(user.uid);
      userType.value = fetchedUserType;

      // Initialize criteria based on user type
      if (fetchedUserType) {
        initializeCriteria(fetchedUserType);
      } else {
        // Default to student criteria if userType is not found
        initializeCriteria("tutor");
      }
    } else {
      currentUser.value = null;
      userType.value = null;
      // Default to student criteria for non-authenticated users
      initializeCriteria("tutor");
    }
    isLoading.value = false;
  });

  // TODO: Fetch session data based on sessionId from route params
  // TODO: Get current user info from auth
});
</script>

<template>
  <!-- Evaluation Form Container -->
  <div class="flex justify-center">
    <!-- Evaluation Form -->
    <div class="eval-form bg-white border-2 border-black rounded-xl shadow-lg overflow-hidden">
      <!-- Student Info Section -->
      <div class="bg-blue-100 p-4 border-b border-gray-200">
        <div class="flex items-start space-x-3">
          <div class="bg-blue-500 rounded-full p-2 flex-shrink-0">
            <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </div>
          <div class="flex-1">
            <div class="flex items-center justify-between mb-2">
              <h2 class="text-lg font-semibold text-blue-900">{{ evaluationTitle }}</h2>
              <div class="text-sm text-blue-600 font-medium">{{ completedCount }}/10 completed</div>
            </div>
            <p class="text-blue-700 text-sm">
              {{ performanceDescription }}
            </p>
          </div>
        </div>
      </div>

      <div class="p-6">
        <!-- Performance Rating Section -->
        <div class="mb-6">
          <div class="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <div class="flex items-start space-x-3">
              <div class="bg-blue-500 rounded-full p-2 flex-shrink-0">
                <svg
                  class="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-base font-semibold text-gray-900 mb-1">
                  Rating Your Student's Performance
                </h3>
                <p class="text-gray-600 text-sm">
                  Rate your agreement with each statement using the 5-point scale.
                </p>
              </div>
            </div>
          </div>

          <!-- Rating Scale Reference -->
          <div class="mb-6">
            <h4 class="font-medium text-gray-900 mb-3 text-sm">Rating Scale Reference:</h4>
            <div
              class="flex justify-around items-center bg-white border border-gray-200 rounded-lg p-4"
            >
              <div class="flex flex-col items-center">
                <div
                  class="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm mb-2"
                >
                  5
                </div>
                <span class="text-gray-700 text-xs font-medium text-center">Strongly Agree</span>
              </div>
              <div class="flex flex-col items-center">
                <div
                  class="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-sm mb-2"
                >
                  4
                </div>
                <span class="text-gray-700 text-xs font-medium text-center">Agree</span>
              </div>
              <div class="flex flex-col items-center">
                <div
                  class="w-10 h-10 bg-gray-400 text-white rounded-full flex items-center justify-center font-bold text-sm mb-2"
                >
                  3
                </div>
                <span class="text-gray-700 text-xs font-medium text-center">Neutral</span>
              </div>
              <div class="flex flex-col items-center">
                <div
                  class="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm mb-2"
                >
                  2
                </div>
                <span class="text-gray-700 text-xs font-medium text-center">Disagree</span>
              </div>
              <div class="flex flex-col items-center">
                <div
                  class="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center font-bold text-sm mb-2"
                >
                  1
                </div>
                <span class="text-gray-700 text-xs font-medium text-center">Strongly Disagree</span>
              </div>
            </div>
          </div>

          <!-- Evaluation Table -->
          <div class="border border-gray-300 rounded-lg overflow-hidden">
            <!-- Table Header -->
            <div class="bg-gray-50 border-b border-gray-300">
              <div class="grid grid-cols-12 gap-4 p-4 items-center">
                <div class="col-span-1 text-center font-medium text-gray-700 text-sm">#</div>
                <div class="col-span-8 font-medium text-gray-700 text-sm">Evaluation</div>
                <div class="col-span-3 text-center font-medium text-gray-700 text-sm">Rating</div>
              </div>
            </div>

            <!-- Criteria Rows -->
            <div class="bg-white">
              <div
                v-for="(criterion, index) in criteria"
                :key="criterion.id"
                class="border-b border-gray-200 last:border-b-0"
              >
                <div class="grid grid-cols-12 gap-4 p-4 items-start">
                  <!-- Number Column -->
                  <div class="col-span-1 flex justify-center pt-1">
                    <span class="text-sm font-medium text-gray-900">{{ index + 1 }}</span>
                  </div>

                  <!-- Evaluation Column -->
                  <div class="col-span-8">
                    <h4 class="font-medium text-gray-900 mb-1 text-sm leading-tight">
                      {{ criterion.title }}
                    </h4>
                    <p class="text-gray-500 text-xs leading-relaxed">
                      {{ criterion.description }}
                    </p>
                  </div>

                  <!-- Rating Column -->
                  <div class="col-span-3">
                    <div class="flex flex-col items-center space-y-3">
                      <!-- Rating Options -->
                      <div class="flex justify-center space-x-2">
                        <div
                          v-for="rating in [5, 4, 3, 2, 1]"
                          :key="rating"
                          class="flex flex-col items-center"
                        >
                          <label class="cursor-pointer">
                            <input
                              type="radio"
                              :name="`rating-${criterion.id}`"
                              :value="rating"
                              @change="updateRating(criterion.id, rating)"
                              :checked="criterion.rating === rating"
                              class="larger-radio"
                              :class="{
                                'text-green-600': rating === 5,
                                'text-blue-600': rating === 4,
                                'text-gray-600': rating === 3,
                                'text-orange-600': rating === 2,
                                'text-red-600': rating === 1,
                              }"
                            />
                          </label>
                          <span
                            class="text-xs font-medium mt-1"
                            :class="{
                              'text-green-600': rating === 5,
                              'text-blue-600': rating === 4,
                              'text-gray-600': rating === 3,
                              'text-orange-600': rating === 2,
                              'text-red-600': rating === 1,
                            }"
                          >
                            {{ rating }}
                          </span>
                        </div>
                      </div>
                      <!-- Clear Button -->
                      <button
                        v-if="criterion.rating !== null"
                        @click="clearRating(criterion.id)"
                        class="clear-btn text-lg text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-md border border-red-200 transition-colors w-30 cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Additional Comments Section -->
        <div class="mb-6 comments-section">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">
            {{ commentsTitle }}
          </h3>
          <p class="text-gray-600 text-xs mb-3 leading-relaxed">
            {{ commentsDescription }}
          </p>
          <div class="relative">
            <textarea
              v-model="additionalComments"
              @input="validateComments"
              @blur="validateComments"
              rows="4"
              maxlength="200"
              :class="[
                'w-full p-3 border rounded-lg focus:ring-1 transition-colors resize-none text-sm',
                profanityError 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200 bg-red-50' 
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
              ]"
              :placeholder="commentsPlaceholder"
            ></textarea>
            
            <!-- Profanity Error Message -->
            <div v-if="profanityError" class="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start gap-2">
                <svg class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div class="flex-1">
                  <p class="text-sm font-semibold text-red-800 mb-1">Inappropriate Content Detected</p>
                  <p class="text-xs text-red-700">{{ profanityError }}</p>
                </div>
              </div>
            </div>
            
            <div class="flex justify-between items-center mt-2">
              <div></div>
              <div
                class="text-xs"
                :class="{
                  'text-gray-500': remainingCharacters >= 100,
                  'text-orange-600': remainingCharacters < 100 && remainingCharacters >= 50,
                  'text-red-600': remainingCharacters < 50,
                }"
              >
                {{ characterCount }}/200 characters
                <span v-if="remainingCharacters < 100" class="ml-1">
                  ({{ remainingCharacters }} remaining)
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Success/Error Messages -->
        <div
          v-if="success"
          class="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg"
        >
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            Feedback submitted successfully! Redirecting...
          </div>
        </div>

        <div v-if="error" class="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {{ error }}
          </div>
        </div>

        <!-- Submit Button -->
        <div class="flex justify-center">
          <button
            @click="handleSubmitFeedback"
            :disabled="isSubmitting || !allCompleted || profanityError !== null"
            :class="[
              'font-medium py-3 px-8 rounded-lg transition-colors text-sm min-w-[200px]',
              allCompleted && !isSubmitting && !profanityError
                ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            ]"
          >
            <span v-if="isSubmitting">Submitting...</span>
            <span v-else>Submit Feedback ({{ completedCount }}/{{ criteria.length }})</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.eval-form {
  width: 60em;
  border: 3px solid #000;
  box-shadow: 6px 8px 0px #000;
}

/* Larger radio button styling */
.larger-radio {
  appearance: none;
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.larger-radio:checked {
  border-color: currentColor;
  background-color: transparent;
}

.larger-radio:checked::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: currentColor;
}

.larger-radio:hover {
  border-color: currentColor;
  opacity: 0.8;
}

/* Color specific styles for larger radio buttons */
.larger-radio.text-green-600:checked {
  border-color: #16a34a;
}

.larger-radio.text-green-600:checked::after {
  background-color: #16a34a;
}

.larger-radio.text-blue-600:checked {
  border-color: #2563eb;
}

.larger-radio.text-blue-600:checked::after {
  background-color: #2563eb;
}

.larger-radio.text-gray-600:checked {
  border-color: #4b5563;
}

.larger-radio.text-gray-600:checked::after {
  background-color: #4b5563;
}

.larger-radio.text-orange-600:checked {
  border-color: #ea580c;
}

.larger-radio.text-orange-600:checked::after {
  background-color: #ea580c;
}

.larger-radio.text-red-600:checked {
  border-color: #dc2626;
}

.larger-radio.text-red-600:checked::after {
  background-color: #dc2626;
}

/* Clear button styling */
.clear-btn {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}
</style>
